/**
 * Inteligência's query hooks, over `/tenants/{tenantId}/ontology`. One query per (tenant, scope)
 * pair -- `post` (Posts, Comentários) and `profile` (Perfis) never share a cache entry, since
 * they carry different dimensions, proposals and settings.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import { om } from '../messages';
import {
  ontologyResponseSchema,
  seedResultSchema,
  type DimensionInput,
  type OntologyProposal,
  type OntologyResponse,
  type OntologyScope,
  type SeedResult,
  type SettingsInput,
  type ValueInput,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

export const ONTOLOGY_QUERY_KEY = (tenantId: string, scope: OntologyScope) =>
  ['tenant', tenantId, 'ontology', scope] as const;

function invalidate(
  queryClient: QueryClient,
  tenantId: string,
  scope: OntologyScope,
): Promise<unknown> {
  return queryClient.invalidateQueries({ queryKey: ONTOLOGY_QUERY_KEY(tenantId, scope) });
}

export function useOntology(
  tenantId: string,
  scope: OntologyScope,
): UseQueryResult<OntologyResponse, ApiFailureError> {
  const client = useApiClient();

  return useQuery<OntologyResponse, ApiFailureError>({
    queryKey: ONTOLOGY_QUERY_KEY(tenantId, scope),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.tenantOntology, { tenantId }, { scope }),
        GET_ROUTES.tenantOntology.schema,
      ),
    enabled: tenantId !== '',
  });
}

export function useSeedOntology(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<SeedResult, ApiFailureError, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<SeedResult, ApiFailureError, void>({
    mutationFn: () => client.postParsed(`/tenants/${tenantId}/ontology/seed`, {}, seedResultSchema),
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

/** One settings row per organisation, read by both scopes' pages, so a save refreshes both. */
export function useUpdateSettings(
  tenantId: string,
): UseMutationResult<SettingsInput, ApiFailureError, SettingsInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const settingsSchema = ontologyResponseSchema.shape.settings;

  return useMutation<SettingsInput, ApiFailureError, SettingsInput>({
    mutationFn: (body) =>
      client.putParsed(`/tenants/${tenantId}/ontology/settings`, body, settingsSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant', tenantId, 'ontology'] }),
  });
}

export interface CreateDimensionArgs {
  name: string;
  extension?: DimensionInput['extension'];
  extract?: boolean;
}

export function useCreateDimension(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<{ id: string }, ApiFailureError, CreateDimensionArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, ApiFailureError, CreateDimensionArgs>({
    mutationFn: (body) =>
      client.postParsed(
        `/tenants/${tenantId}/ontology/dimensions`,
        { ...body, scope },
        z.object({ id: z.uuid() }),
      ),
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export interface UpdateDimensionArgs {
  dimensionId: string;
  body: DimensionInput;
}

export function useUpdateDimension(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, UpdateDimensionArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, UpdateDimensionArgs>({
    mutationFn: async ({ dimensionId, body }) => {
      await client.patchParsed(
        `/tenants/${tenantId}/ontology/dimensions/${dimensionId}`,
        body,
        z.undefined(),
      );
    },
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export function useDeleteDimension(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: (dimensionId) =>
      client.del(`/tenants/${tenantId}/ontology/dimensions/${dimensionId}`),
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export interface CreateValueArgs {
  dimensionId: string;
  name: string;
  parent_id?: string;
  aliases?: string[];
}

export function useCreateValue(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<{ id: string }, ApiFailureError, CreateValueArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, ApiFailureError, CreateValueArgs>({
    mutationFn: ({ dimensionId, ...body }) =>
      client.postParsed(
        `/tenants/${tenantId}/ontology/dimensions/${dimensionId}/values`,
        body,
        z.object({ id: z.uuid() }),
      ),
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export interface UpdateValueArgs {
  valueId: string;
  body: ValueInput;
}

export function useUpdateValue(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, UpdateValueArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, UpdateValueArgs>({
    mutationFn: async ({ valueId, body }) => {
      await client.patchParsed(
        `/tenants/${tenantId}/ontology/values/${valueId}`,
        body,
        z.undefined(),
      );
    },
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export function useDeleteValue(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: (valueId) => client.del(`/tenants/${tenantId}/ontology/values/${valueId}`),
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export interface MergeValueArgs {
  valueId: string;
  into: string;
}

export function useMergeValue(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, MergeValueArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, MergeValueArgs>({
    mutationFn: async ({ valueId, into }) => {
      await client.postParsed(
        `/tenants/${tenantId}/ontology/values/${valueId}/merge`,
        { into },
        z.undefined(),
      );
    },
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

/**
 * Approves with no destination: the server nests the value under the parent the analysis
 * suggested, or at the dimension's root. The proposal leaves the queue before the server
 * answers and returns if it refuses. The toasts sit on the hook, not on `mutate`: the row that
 * fired it has already unmounted, and `mutate`'s own callbacks never fire after that.
 */
type AcceptVariables = { proposal: OntologyProposal; dimensionName: string };

export function useAcceptProposal(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, AcceptVariables, { previous?: OntologyResponse }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const key = ONTOLOGY_QUERY_KEY(tenantId, scope);

  return useMutation<void, ApiFailureError, AcceptVariables, { previous?: OntologyResponse }>({
    mutationFn: async ({ proposal }) => {
      await client.postParsed(
        `/tenants/${tenantId}/ontology/proposals/${proposal.id}/accept`,
        {},
        z.undefined(),
      );
    },
    onMutate: async ({ proposal: accepted }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OntologyResponse>(key);
      if (previous !== undefined) {
        queryClient.setQueryData<OntologyResponse>(key, {
          ...previous,
          proposals: previous.proposals.filter((proposal) => proposal.id !== accepted.id),
        });
      }
      return { previous };
    },
    onSuccess: (_data, { dimensionName }) => {
      toast.success(om('proposals.approved', { dimension: dimensionName }));
    },
    onError: (_error, { proposal }, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
      toast.error(om('proposals.approve.failed', { name: proposal.name }));
    },
    onSettled: () => invalidate(queryClient, tenantId, scope),
  });
}

export function useMergeProposal(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, { proposalId: string; into: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, { proposalId: string; into: string }>({
    mutationFn: async ({ proposalId, into }) => {
      await client.postParsed(
        `/tenants/${tenantId}/ontology/proposals/${proposalId}/merge`,
        { into },
        z.undefined(),
      );
    },
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}

export function useRejectProposal(
  tenantId: string,
  scope: OntologyScope,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: async (proposalId) => {
      await client.postParsed(
        `/tenants/${tenantId}/ontology/proposals/${proposalId}/reject`,
        {},
        z.undefined(),
      );
    },
    onSuccess: () => invalidate(queryClient, tenantId, scope),
  });
}
